import { apiClient } from './client'
import type {
  CreationCreateRequest,
  CreationTaskDetail,
  CreationTaskFilters,
  CreationTaskList,
  CreationTaskSummary,
  CreationWorkFilters,
  CreationWorkList,
  ApiWorkSummary,
} from '@/lib/types/creation'

const BASE = '/agent-api/creation'

export const creationApi = {
  // ── Tasks ──────────────────────────────────────────────────

  createTask: async (payload: CreationCreateRequest) => {
    const response = await apiClient.post<CreationTaskSummary>(
      `${BASE}/tasks`,
      payload,
    )
    return response.data
  },

  getTask: async (id: string) => {
    const response = await apiClient.get<CreationTaskDetail>(
      `${BASE}/tasks/${id}`,
    )
    return response.data
  },

  listTasks: async (params: CreationTaskFilters = {}) => {
    const response = await apiClient.get<CreationTaskList>(
      `${BASE}/tasks`,
      { params },
    )
    return response.data
  },

  cancelTask: async (id: string) => {
    const response = await apiClient.post<CreationTaskSummary>(
      `${BASE}/tasks/${id}/cancel`,
    )
    return response.data
  },

  retryTask: async (id: string) => {
    const response = await apiClient.post<CreationTaskSummary>(
      `${BASE}/tasks/${id}/retry`,
    )
    return response.data
  },

  deleteTask: async (id: string) => {
    await apiClient.delete(`${BASE}/tasks/${id}`)
  },

  // ── Works ──────────────────────────────────────────────────

  listWorks: async (params: CreationWorkFilters = {}) => {
    const response = await apiClient.get<CreationWorkList>(
      `${BASE}/works`,
      { params },
    )
    return response.data
  },

  getWork: async (id: string) => {
    const response = await apiClient.get<ApiWorkSummary>(
      `${BASE}/works/${id}`,
    )
    return response.data
  },

  setFavorite: async (id: string, favorite: boolean) => {
    const response = await apiClient.put<ApiWorkSummary>(
      `${BASE}/works/${id}/favorite`,
      { favorite },
    )
    return response.data
  },

  deleteWork: async (id: string) => {
    await apiClient.delete(`${BASE}/works/${id}`)
  },

  // ── Prompts ──────────────────────────────────────────────────

  listPrompts: async () => {
    const response = await apiClient.get<import('@/lib/types/creation').CreationPrompt[]>(
      '/agent-api/creation/prompts',
    )
    return response.data
  },

  updatePrompt: async (
    id: string,
    data: { name?: string; description?: string; content?: string; version?: number },
  ) => {
    const response = await apiClient.put<import('@/lib/types/creation').CreationPrompt>(
      `/agent-api/creation/prompts/${id}`,
      data,
    )
    return response.data
  },

  previewPrompt: async (content: string, variables: Record<string, string>) => {
    const response = await apiClient.post<{
      rendered: string; variables: string[]; valid: boolean; error: string
    }>('/agent-api/creation/prompts/preview', { content, variables })
    return response.data
  },

  restoreDefaultPrompt: async (id: string) => {
    const response = await apiClient.post<import('@/lib/types/creation').CreationPrompt>(
      `/agent-api/creation/prompts/${id}/restore-default`,
    )
    return response.data
  },

  listPromptVersions: async (id: string) => {
    const response = await apiClient.get<import('@/lib/types/creation').CreationPromptRevision[]>(
      `/agent-api/creation/prompts/${id}/versions`,
    )
    return response.data
  },

  generatePrompts: async (
    id: string,
    content: string,
    variables: Record<string, string>,
    assets: { download_url: string; preview_url?: string; name: string; mime_type?: string }[],
  ) => {
    const response = await apiClient.post<import('@/lib/types/creation').CreationGenerateResult>(
      `/agent-api/creation/prompts/${id}/generate`,
      { content, variables, assets },
    )
    return response.data
  },
}
